import React, { useRef, useCallback, useMemo } from "react";
import classNames from "classnames";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../shared/Button";

/**
 * Reference token format: @[type:id|label]
 */
const REFERENCE_REGEX = /@\[([a-z]+):([^\]|]+)\|([^\]]+)\]/gi;

/**
 * Get display color for reference type pill.
 */
function getRefTypeColor(refType) {
  const colors = {
    job: "bg-blue-100 text-blue-700 border-blue-200",
    manifest: "bg-purple-100 text-purple-700 border-purple-200",
    invoice: "bg-green-100 text-green-700 border-green-200",
    payout: "bg-amber-100 text-amber-700 border-amber-200",
    equipment: "bg-slate-100 text-slate-700 border-slate-200",
    client: "bg-rose-100 text-rose-700 border-rose-200",
    user: "bg-cyan-100 text-cyan-700 border-cyan-200",
    leg: "bg-orange-100 text-orange-700 border-orange-200",
  };
  return colors[refType] || "bg-gray-100 text-gray-700 border-gray-200";
}

/**
 * Parse references from text.
 */
function parseReferences(text) {
  const refs = [];
  const regex = new RegExp(REFERENCE_REGEX.source, "gi");
  let match;
  while ((match = regex.exec(text)) !== null) {
    refs.push({
      token: match[0],
      type: match[1],
      id: match[2],
      label: match[3],
    });
  }
  return refs;
}

/**
 * Get a cleaner display version of the text for the textarea.
 * Replaces @[type:id|label] with @label for easier reading.
 */
function getDisplayText(text, refs) {
  let display = text;
  refs.forEach((ref) => {
    display = display.replace(ref.token, `@${ref.label}`);
  });
  return display;
}

/**
 * Convert display text back to token format.
 * Maps @label back to the full token.
 */
function toTokenText(displayText, refs, originalText) {
  // If no refs, just return the display text as-is
  if (!refs.length) return displayText;
  
  let result = displayText;
  refs.forEach((ref) => {
    // Replace @label with the full token
    result = result.replace(`@${ref.label}`, ref.token);
  });
  return result;
}

/**
 * Reference Chip component.
 */
function ReferenceChip({ refType, label, onRemove }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border",
        getRefTypeColor(refType)
      )}
    >
      <span className="opacity-60 text-[10px] uppercase">{refType}</span>
      <span className="max-w-[100px] truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 hover:opacity-100 opacity-60 transition-opacity"
          tabIndex={-1}
          aria-label={`Remove ${label} reference`}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

/**
 * ChatComposer - Chat input with reference pills shown above the textarea.
 */
export default function ChatComposer({
  value = "",
  onChange,
  onAtClick,
  onSend,
  sendLoading,
  sendDisabled,
  placeholder = "Type a message...",
  inputRef: externalRef,
}) {
  const internalRef = useRef(null);
  const textareaRef = externalRef || internalRef;

  // Parse references from the stored value
  const references = useMemo(() => parseReferences(value), [value]);
  
  // Get display text (cleaner format for textarea)
  const displayText = useMemo(
    () => getDisplayText(value, references),
    [value, references]
  );

  /**
   * Handle textarea change - convert display format back to token format.
   */
  const handleChange = useCallback(
    (e) => {
      const newDisplayText = e.target.value;
      // Convert back to token format
      const tokenText = toTokenText(newDisplayText, references, value);
      onChange?.(tokenText);
    },
    [onChange, references, value]
  );

  /**
   * Handle keydown for Enter to send.
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!sendDisabled) {
          onSend?.();
        }
      }
    },
    [onSend, sendDisabled]
  );

  /**
   * Remove a reference from the text.
   */
  const removeReference = useCallback(
    (token) => {
      const newValue = value.replace(token, "").replace(/\s{2,}/g, " ").trim();
      onChange?.(newValue);
      // Focus textarea after removal
      textareaRef.current?.focus();
    },
    [value, onChange, textareaRef]
  );

  return (
    <div className="space-y-2">
      {/* Reference chips shown above the textarea */}
      {references.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-1">
          {references.map((ref, idx) => (
            <ReferenceChip
              key={`${ref.type}-${ref.id}-${idx}`}
              refType={ref.type}
              label={ref.label}
              onRemove={() => removeReference(ref.token)}
            />
          ))}
        </div>
      )}

      {/* Textarea with integrated buttons */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={displayText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          className={classNames(
            "w-full resize-none",
            "px-3 py-2.5 pr-[88px]",
            "text-sm text-gray-900 leading-relaxed",
            "border border-gray-200 rounded-xl",
            "focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400",
            "bg-white transition-colors",
            "placeholder:text-gray-400"
          )}
          style={{ minHeight: "56px" }}
        />

        {/* Buttons inside the textarea area */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
          <button
            type="button"
            onClick={onAtClick}
            title="Add reference (@)"
            aria-label="Add reference"
            className={classNames(
              "h-7 w-7 shrink-0 inline-flex items-center justify-center",
              "rounded-md bg-gray-100 text-gray-500",
              "hover:bg-gray-200 hover:text-gray-700",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
              "transition-colors"
            )}
          >
            <span className="text-sm font-semibold leading-none">@</span>
          </button>
          <Button
            size="sm"
            type="primary"
            loading={sendLoading}
            disabled={sendDisabled}
            onClick={onSend}
            className="h-7 px-3 text-xs"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
